import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import CourseScheduleTable from '../CourseScheduleTable/CourseScheduleTable';
import { Course } from '../../../types';

 

/**
 * Recommended Courses Section
 * Displays course schedule table with filtering and pagination
 * Matches original UI with animations and gradient styling
 */


const RecommendedCourses: React.FC = () => {
  // Animation setup
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: false, amount: 0.3 });

  return (
    <section
      ref={sectionRef}
      className="py-32 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 overflow-hidden"
      id="courses"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-8 mb-16">
          {/* Animated small subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center text-sm font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400"
          >
            Các lớp học IELTS sắp khai giảng
          </motion.p>

          {/* Enhanced main heading with animated parts */}
          <div className="relative">
            {/* Background decorative elements */}
            <div className="absolute -top-20 -left-20 w-40 h-40 rounded-full bg-gradient-to-br from-teal-300/20 to-teal-500/20 blur-3xl dark:from-teal-700/20 dark:to-teal-900/20"></div>
            <div className="absolute -bottom-20 -right-20 w-40 h-40 rounded-full bg-gradient-to-tr from-emerald-400/20 to-cyan-400/20 blur-3xl dark:from-emerald-700/20 dark:to-cyan-700/20"></div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-center text-2xl font-bold sm:text-3xl lg:text-4xl text-gray-800 dark:text-gray-100"
            >
              Lịch khai giảng{' '}
              <motion.span
                initial={{
                  backgroundPosition: '0% 100%',
                  opacity: 0,
                }}
                animate={
                  isInView
                    ? {
                        backgroundPosition: '100% 100%',
                        opacity: 1,
                      }
                    : {}
                }
                transition={{
                  duration: 1.2,
                  delay: 0.5,
                  ease: 'easeOut',
                }}
                className="relative"
              >
                <span className="relative z-10 bg-gradient-to-r from-[#017e6e] via-[#01a798] to-emerald-400 dark:from-emerald-300 dark:via-teal-400 dark:to-cyan-300 bg-clip-text text-transparent">
                  các khóa học
                </span>
              </motion.span>{' '}
              tại Trung Tâm
            </motion.h2>
          </div>

          {/* Animated description paragraph */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mx-auto max-w-2xl text-center text-base sm:text-lg lg:text-xl leading-relaxed text-gray-600 dark:text-gray-300"
          >
            Tại Anh Ngữ JENNIFER, các khóa học IELTS được thiết kế bài bản, giúp bạn chinh phục mục tiêu điểm số mong muốn trong thời gian phù hợp với năng lực và lịch trình cá nhân.
          </motion.p>

          {/* Decorative animated dot indicators */}
          <div className="flex justify-center space-x-2">
            {[0, 1, 2, 3, 4].map((dot) => (
              <motion.div
                key={dot}
                initial={{ scale: 0, opacity: 0 }}
                animate={isInView ? { scale: 1, opacity: 1 } : {}}
                transition={{
                  duration: 0.4,
                  delay: 0.8 + dot * 0.1,
                  type: 'spring',
                  stiffness: 400,
                  damping: 10,
                }}
                className={`w-2 h-2 rounded-full ${
                  dot % 2 === 0
                    ? 'bg-gradient-to-r from-teal-500 to-emerald-400 dark:from-teal-400 dark:to-emerald-300'
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}
              ></motion.div>
            ))}
          </div>
        </div>

        {/* Course Schedule Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <CourseScheduleTable courceCategory="ielts" />
        </motion.div>
      </div>
    </section>
  );
};

export default RecommendedCourses;
